import { useState, useEffect, useMemo, useRef, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import RangeDatePicker from "@/components/base/RangeDatePicker";
import TimeInput24 from "@/components/base/TimeInput24";
import NationalitySelect from "@/components/base/NationalitySelect";
import { parseISODate, toISODate } from "@/components/base/DatePicker";
import { LocalizedLink } from "@/i18n/language";
import CheckInOutNote from "@/components/feature/CheckInOutNote";
import TransferPicker, {
  transferKeyOf,
  type TransferCategory,
  type CarCount,
} from "./TransferPicker";
import { formatAmount } from "@/lib/currency";
import { useTourBlockedDates } from "@/hooks/useTourBlockedDates";
import {
  getBausteinPrices,
  getVillaAvailability,
  getVillaBlockedDates,
  submitBausteinInquiry,
  type BausteinPrice,
} from "@/lib/villaApi";

type Status = { type: "idle" | "sending" | "success" | "error"; msg: string };
type Step = 0 | 1 | 2 | 3 | 4 | 5;
type ExcursionKey = "day_trip" | "rafting" | "sailing" | "massage" | "dinner";
type MultiDayKey = "day_trip" | "massage" | "dinner";
type BookingKey = "rafting" | "sailing";

type ExcursionsState = {
  day_trip: { days: number; persons: number };
  massage: { days: number; persons: number };
  dinner: { days: number; persons: number };
  rafting: number;
  sailing: number;
};

const MULTIDAY_KEYS: MultiDayKey[] = ["day_trip", "massage", "dinner"];

const WHOLE_PROPERTY_SLUG = "villa-gesamt";

type VillaOption = { key: string; slug: string; maxPersons: number; wide?: boolean };

const VILLA_OPTIONS: VillaOption[] = [
  { key: "villa_bougainvillea_1", slug: "villa-bougainvillea", maxPersons: 2 },
  { key: "villa_frangipani_1", slug: "villa-frangipani", maxPersons: 2 },
  { key: "villa_frangipani_2", slug: "villa-frangipani", maxPersons: 4 },
  { key: "villa_hibiskus_2", slug: "villa-hibiskus", maxPersons: 4 },
  { key: "villa_hibiskus_3", slug: "villa-hibiskus", maxPersons: 6 },
  { key: "villa_gesamt", slug: WHOLE_PROPERTY_SLUG, maxPersons: 12, wide: true },
];

// Slugs loaded from the backend to build the availability of the selected option.
const AVAILABILITY_SLUGS = [
  "villa-frangipani",
  "villa-bougainvillea",
  "villa-hibiskus",
  WHOLE_PROPERTY_SLUG,
];

// Which slugs make a selected option unavailable: the whole property is blocked by
// every villa, and each single villa is blocked by a whole-property booking.
const BLOCKING_SOURCES: Record<string, string[]> = {
  "villa-frangipani": ["villa-frangipani", WHOLE_PROPERTY_SLUG],
  "villa-bougainvillea": ["villa-bougainvillea", WHOLE_PROPERTY_SLUG],
  "villa-hibiskus": ["villa-hibiskus", WHOLE_PROPERTY_SLUG],
  [WHOLE_PROPERTY_SLUG]: ["villa-frangipani", "villa-bougainvillea", "villa-hibiskus", WHOLE_PROPERTY_SLUG],
};

const EXCURSION_ITEMS: { key: ExcursionKey; kind: "multiday" | "booking"; icon: string }[] = [
  { key: "day_trip", kind: "multiday", icon: "ri-map-pin-line" },
  { key: "rafting", kind: "booking", icon: "ri-water-flash-line" },
  { key: "sailing", kind: "booking", icon: "ri-sailboat-line" },
  { key: "massage", kind: "multiday", icon: "ri-hand-heart-line" },
  { key: "dinner", kind: "multiday", icon: "ri-restaurant-line" },
];

const BLOCK_LABEL_KEYS: Record<string, string> = {
  villa: "baustein_block_villa_title",
  ausfluege: "baustein_block_ausfluege_title",
  touren: "baustein_block_touren_title",
  massage_dinner: "baustein_block_massage_dinner_title",
  transfers: "baustein_block_transfers_title",
  vornach: "baustein_block_vornach_title",
};

export default function BausteineConfigurator() {
  const { t, i18n } = useTranslation();
  const isDe = i18n.language.startsWith("de");
  const [searchParams] = useSearchParams();

  const stepsRef = useRef<HTMLDivElement>(null);
  const successRef = useRef<HTMLDivElement>(null);
  const firstStepRender = useRef(true);

  const [prices, setPrices] = useState<BausteinPrice[]>([]);
  const [priceLoading, setPriceLoading] = useState(true);
  const [priceError, setPriceError] = useState(false);

  const [step, setStep] = useState<Step>(0);
  const [villa, setVilla] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [persons, setPersons] = useState(2);
  const [excursions, setExcursions] = useState<ExcursionsState>({
    day_trip: { days: 0, persons: 2 },
    massage: { days: 0, persons: 2 },
    dinner: { days: 0, persons: 2 },
    rafting: 0,
    sailing: 0,
  });
  const [arrivalTransfer, setArrivalTransfer] = useState<TransferCategory>("none");
  const [arrivalCars, setArrivalCars] = useState<CarCount>(1);
  const [departureTransfer, setDepartureTransfer] = useState<TransferCategory>("none");
  const [departureCars, setDepartureCars] = useState<CarCount>(1);
  const [arrivalFlight, setArrivalFlight] = useState({ airline: "", number: "", time: "" });
  const [departureFlight, setDepartureFlight] = useState({ airline: "", number: "", time: "" });
  const [arrivalPickup, setArrivalPickup] = useState("");
  const [departurePickup, setDeparturePickup] = useState("");
  const [arrivalPickupTime, setArrivalPickupTime] = useState("");
  const [departurePickupTime, setDeparturePickupTime] = useState("");

  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [preProgramWish, setPreProgramWish] = useState("");
  const [postProgramWish, setPostProgramWish] = useState("");
  const [terms, setTerms] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [stepError, setStepError] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>({ type: "idle", msg: "" });

  const [rawAvailability, setRawAvailability] = useState<
    Record<string, { reservations: { check_in: string; check_out: string }[]; blockedDates: string[] }>
  >({});

  // Global gesperrte Tour-Tage (Bali Holiday, Ruhetage) – gilt für alle Touren.
  const tourBlockedDates = useTourBlockedDates();

  const today = new Date().toISOString().split("T")[0];
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 2);
  const maxDateStr = maxDate.toISOString().split("T")[0];

  const inputClass =
    "w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-colors";
  const labelClass = "block text-sm font-medium text-foreground-800 mb-1.5";

  const selectedBlocks = useMemo(() => {
    const raw = searchParams.get("blocks") ?? "";
    return raw.split(",").filter((b) => BLOCK_LABEL_KEYS[b]);
  }, [searchParams]);

  const hasTransfer = selectedBlocks.includes("transfers");
  const hasVornach = selectedBlocks.includes("vornach");

  const stepItems = useMemo(() => {
    const items: { num: Step; label: string }[] = [
      { num: 0, label: isDe ? "Villa" : "Villa" },
      { num: 1, label: isDe ? "Zeitraum & Personen" : "Period & people" },
      { num: 2, label: isDe ? "Ausflüge" : "Excursions" },
    ];
    if (hasTransfer) {
      items.push({ num: 3, label: isDe ? "Transfer" : "Transfer" });
    }
    items.push({ num: 4, label: isDe ? "Kontakt" : "Contact" });
    items.push({ num: 5, label: isDe ? "Bestätigen" : "Confirm" });
    return items;
  }, [isDe, hasTransfer]);

  // Load prices
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setPriceLoading(true);
      setPriceError(false);
      const { data, error } = await getBausteinPrices();
      if (cancelled) return;
      if (error || !data) {
        setPriceError(true);
        setPriceLoading(false);
        return;
      }
      setPrices(data);
      setPriceLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const exchangeRate = useMemo(() => {
    const row = prices.find((p) => p.key === "eur_to_usd_rate" || p.unit === "rate");
    const rate = row ? Number(row.amount) : NaN;
    return Number.isFinite(rate) && rate > 0 ? rate : 1.0753;
  }, [prices]);

  const priceMap = useMemo(() => {
    const m: Record<string, number> = {};
    prices.forEach((p) => {
      const isCurrency = p.unit !== "percent" && p.unit !== "nights" && p.unit !== "rate";
      const eur = Number(p.amount);
      m[p.key] = !isDe && isCurrency ? Math.round(eur * exchangeRate) : eur;
    });
    return m;
  }, [prices, isDe, exchangeRate]);

  const money = (amount: number) => formatAmount(amount, isDe);

  const discountNights = priceMap["discount_nights"] || 5;
  const discountPercent = (priceMap["discount_percent"] || 5) / 100;
  const discountPercentLabel = priceMap["discount_percent"] || 5;
  const discountExcursions = priceMap["discount_excursions"] || 3;

  const labelOf = (key: string): string => {
    const p = prices.find((pr) => pr.key === key);
    return p ? (isDe ? p.label_de : p.label_en) : key;
  };

  const priceOf = (key: string): number => priceMap[key] ?? 0;

  const arrivalTransferKey = transferKeyOf(arrivalTransfer, arrivalCars);
  const departureTransferKey = transferKeyOf(departureTransfer, departureCars);

  const villaSlug = VILLA_OPTIONS.find((v) => v.key === villa)?.slug ?? "villa-frangipani";
  const maxPersons = VILLA_OPTIONS.find((v) => v.key === villa)?.maxPersons ?? 6;

  // Clamp persons when villa changes
  useEffect(() => {
    setPersons((p) => Math.min(p, maxPersons));
  }, [maxPersons]);

  // Clamp excursion persons when the group size shrinks
  useEffect(() => {
    setExcursions((prev) => {
      const next: ExcursionsState = {
        ...prev,
        day_trip: { ...prev.day_trip },
        massage: { ...prev.massage },
        dinner: { ...prev.dinner },
      };
      let changed = false;
      MULTIDAY_KEYS.forEach((k) => {
        if (next[k].persons > persons) {
          next[k] = { ...next[k], persons: persons };
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [persons]);

  // Load availability for every villa once (the whole property combines all of them).
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const entries = await Promise.all(
          AVAILABILITY_SLUGS.map(async (slug) => {
            const [resRes, blockRes] = await Promise.all([
              getVillaAvailability(slug),
              getVillaBlockedDates(slug),
            ]);
            const value =
              resRes.error || blockRes.error
                ? { reservations: [], blockedDates: [] }
                : {
                    reservations: (resRes.data ?? []) as { check_in: string; check_out: string }[],
                    blockedDates: ((blockRes.data ?? []) as { blocked_date: string }[]).map(
                      (d) => d.blocked_date
                    ),
                  };
            return [slug, value] as const;
          })
        );
        if (cancelled) return;
        setRawAvailability(Object.fromEntries(entries));
      } catch {
        if (!cancelled) setRawAvailability({});
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Merge the availability of all blocking sources for the selected villa.
  const availability = useMemo(() => {
    const sources = BLOCKING_SOURCES[villaSlug] ?? [villaSlug];
    const reservations: { check_in: string; check_out: string }[] = [];
    const blockedDates: string[] = [];
    for (const s of sources) {
      const a = rawAvailability[s];
      if (!a) continue;
      reservations.push(...a.reservations);
      blockedDates.push(...a.blockedDates);
    }
    return { reservations, blockedDates };
  }, [rawAvailability, villaSlug]);

  const disabledDates = useMemo(() => {
    const set = new Set<string>();
    for (const d of availability.blockedDates) set.add(d);
    for (const r of availability.reservations) {
      const s = parseISODate(r.check_in);
      const e = parseISODate(r.check_out);
      if (!s || !e) continue;
      const cur = new Date(s);
      while (cur < e) {
        set.add(toISODate(cur));
        cur.setDate(cur.getDate() + 1);
      }
    }
    return set;
  }, [availability]);

  // firstOccupiedNight: the earliest night after `start` that is already booked/blocked.
  const firstOccupiedNight = useMemo(() => {
    if (!start) return null;
    const startDate = parseISODate(start);
    if (!startDate) return null;
    let first: Date | null = null;

    for (const d of availability.blockedDates) {
      const bd = parseISODate(d);
      if (bd && bd > startDate && (!first || bd < first)) first = bd;
    }

    for (const r of availability.reservations) {
      const s = parseISODate(r.check_in);
      const e = parseISODate(r.check_out);
      if (!s || !e) continue;
      if (s > startDate && (!first || s < first)) first = new Date(s);
      const cur = new Date(s);
      while (cur < e) {
        if (cur > startDate && (!first || cur < first)) first = new Date(cur);
        cur.setDate(cur.getDate() + 1);
      }
    }

    return first ? toISODate(first) : null;
  }, [availability, start]);

  // maxCheckout: allow selecting the turnover day (firstOccupiedNight) as departure — the half-day checkout.
  const maxCheckout = useMemo(() => {
    return firstOccupiedNight ?? maxDateStr;
  }, [firstOccupiedNight, maxDateStr]);

  // Departure half-day: the first occupied night after check-in is the turnover day.
  const checkoutBookedDates = useMemo(() => {
    const set = new Set(disabledDates);
    if (firstOccupiedNight) set.delete(firstOccupiedNight);
    return set;
  }, [disabledDates, firstOccupiedNight]);

  // Turnover dates for check-in calendar.
  const checkInTurnoverDates = useMemo(() => {
    return new Set(availability.reservations.map((r) => r.check_out));
  }, [availability.reservations]);

  // Turnover dates for check-out calendar.
  const checkOutTurnoverDates = useMemo(() => {
    if (!firstOccupiedNight) return new Set<string>();
    return new Set([firstOccupiedNight]);
  }, [firstOccupiedNight]);

  // Turnover day for check-in.
  const isTurnoverDay = useMemo(() => {
    if (!start) return false;
    const startStr = toISODate(parseISODate(start) ?? new Date());
    return availability.reservations.some((r) => {
      const checkOut = parseISODate(r.check_out);
      return checkOut && toISODate(checkOut) === startStr;
    });
  }, [availability, start]);

  // Turnover day for check-out.
  const isCheckoutTurnoverDay = useMemo(() => {
    if (!end || !firstOccupiedNight) return false;
    const endStr = toISODate(parseISODate(end) ?? new Date());
    const firstOccupiedStr = toISODate(parseISODate(firstOccupiedNight) ?? new Date());
    return endStr === firstOccupiedStr;
  }, [end, firstOccupiedNight]);

  const nights = useMemo(() => {
    if (!start || !end) return 0;
    const a = parseISODate(start);
    const b = parseISODate(end);
    if (!a || !b) return 0;
    return Math.round((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24));
  }, [start, end]);

  // Gesperrte Tour-Tage, die in den Aufenthalt fallen (An- und Abreisetag ausgenommen).
  const tourBlockedInStay = useMemo(() => {
    if (!start || !end || tourBlockedDates.size === 0) return [] as string[];
    const s = parseISODate(start);
    const e = parseISODate(end);
    if (!s || !e) return [] as string[];
    return Array.from(tourBlockedDates)
      .filter((iso) => {
        const d = parseISODate(iso);
        return !!d && d > s && d < e;
      })
      .sort();
  }, [start, end, tourBlockedDates]);

  const formatBlockedDay = (iso: string): string => {
    const d = parseISODate(iso);
    if (!d) return iso;
    return d.toLocaleDateString(isDe ? "de-DE" : "en-GB", { day: "2-digit", month: "2-digit" });
  };

  // Clamp excursion days when the stay length shrinks
  useEffect(() => {
    setExcursions((prev) => {
      const next: ExcursionsState = {
        ...prev,
        day_trip: { ...prev.day_trip },
        massage: { ...prev.massage },
        dinner: { ...prev.dinner },
      };
      let changed = false;

      const budget = Math.max(0, nights - 1 - tourBlockedInStay.length);
      let bookedDays = (next.rafting > 0 ? 1 : 0) + (next.sailing > 0 ? 1 : 0);
      if (bookedDays > budget) {
        next.rafting = 0;
        next.sailing = 0;
        bookedDays = 0;
        changed = true;
      }

      MULTIDAY_KEYS.forEach((k) => {
        const max =
          k === "day_trip"
            ? Math.max(0, budget - bookedDays)
            : Math.max(0, nights);
        if (next[k].days > max) {
          next[k] = { ...next[k], days: max };
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [nights, tourBlockedInStay.length]);

  // Ausflugstage: Tagesausflüge, Rafting und Segeln teilen sich das Budget von
  // (Nächte - 1) Tagen, abzüglich der gesperrten Tour-Tage im Aufenthalt.
  const excursionDayBudget = Math.max(0, nights - 1 - tourBlockedInStay.length);
  const bookingDaysUsed =
    (excursions.rafting > 0 ? 1 : 0) + (excursions.sailing > 0 ? 1 : 0);
  const remainingExcursionDays = Math.max(
    0,
    excursionDayBudget - bookingDaysUsed - excursions.day_trip.days
  );

  // Maximale Tage je Kategorie: Dinner & Massage täglich, Tagesausflüge teilen sich das Tagesbudget mit Rafting/Segeln.
  const maxDaysFor = (key: MultiDayKey): number => {
    if (key === "day_trip") return Math.max(0, excursionDayBudget - bookingDaysUsed);
    return Math.max(0, nights);
  };

  const incrementDays = (key: MultiDayKey) => {
    setExcursions((prev) => {
      const cur = prev[key];
      if (cur.days >= maxDaysFor(key)) return prev;
      return { ...prev, [key]: { ...cur, days: cur.days + 1 } };
    });
  };

  const decrementDays = (key: MultiDayKey) => {
    setExcursions((prev) => {
      const cur = prev[key];
      if (cur.days <= 0) return prev;
      return { ...prev, [key]: { ...cur, days: cur.days - 1 } };
    });
  };

  const incrementPersons = (key: MultiDayKey) => {
    setExcursions((prev) => {
      const cur = prev[key];
      if (cur.persons >= persons) return prev;
      return { ...prev, [key]: { ...cur, persons: cur.persons + 1 } };
    });
  };

  const decrementPersons = (key: MultiDayKey) => {
    setExcursions((prev) => {
      const cur = prev[key];
      if (cur.persons <= 1) return prev;
      return { ...prev, [key]: { ...cur, persons: cur.persons - 1 } };
    });
  };

  const incrementBooking = (key: BookingKey) => {
    setExcursions((prev) => {
      const maxP = Math.min(2, persons);
      if (prev[key] >= maxP) return prev;
      if (prev[key] === 0) {
        const otherBooked =
          key === "rafting" ? (prev.sailing > 0 ? 1 : 0) : (prev.rafting > 0 ? 1 : 0);
        if (prev.day_trip.days + otherBooked + 1 > excursionDayBudget) return prev;
      }
      return { ...prev, [key]: prev[key] + 1 };
    });
  };

  const decrementBooking = (key: BookingKey) => {
    setExcursions((prev) => {
      if (prev[key] <= 0) return prev;
      return { ...prev, [key]: prev[key] - 1 };
    });
  };

  const excursionAmount = (key: ExcursionKey): number => {
    if (key === "rafting") return priceOf(key) * excursions.rafting;
    if (key === "sailing") return priceOf(key) * excursions.sailing;
    const e = excursions[key];
    return priceOf(key) * e.days * e.persons;
  };

  const excursionDisplay = (
    key: ExcursionKey
  ): { label: string; detail: string; amount: number } | null => {
    if (key === "rafting" || key === "sailing") {
      const ppl = excursions[key];
      if (ppl <= 0) return null;
      return {
        label: labelOf(key),
        detail: `${ppl} ${isDe ? "Pers." : "ppl."}`,
        amount: excursionAmount(key),
      };
    }
    const e = excursions[key];
    if (e.days <= 0 || e.persons <= 0) return null;
    return {
      label: labelOf(key),
      detail: `${e.days} ${isDe ? "Tage" : "days"} × ${e.persons} ${isDe ? "Pers." : "ppl."}`,
      amount: excursionAmount(key),
    };
  };

  const calc = useMemo(() => {
    const villaAmount = priceOf(villa) * nights;
    const dayTripAmount = excursionAmount("day_trip");
    const massageAmount = excursionAmount("massage");
    const dinnerAmount = excursionAmount("dinner");
    const raftingAmount = excursionAmount("rafting");
    const sailingAmount = excursionAmount("sailing");
    const tripAmount = dayTripAmount + massageAmount + raftingAmount + sailingAmount;
    const arrivalAmount = hasTransfer && arrivalTransfer !== "none" ? priceOf(arrivalTransferKey) : 0;
    const departureAmount = hasTransfer && departureTransfer !== "none" ? priceOf(departureTransferKey) : 0;
    const subtotal =
      villaAmount + tripAmount + dinnerAmount + arrivalAmount + departureAmount;
    const discountApplied =
      nights >= discountNights && excursions.day_trip.days >= discountExcursions;
    const discount = discountApplied ? Math.round(subtotal * discountPercent) : 0;
    const total = subtotal - discount;
    return {
      villaAmount,
      tripAmount,
      dinnerAmount,
      dayTripAmount,
      massageAmount,
      raftingAmount,
      sailingAmount,
      arrivalAmount,
      departureAmount,
      subtotal,
      discountApplied,
      discount,
      total,
    };
  }, [
    villa,
    nights,
    persons,
    excursions,
    arrivalTransfer,
    departureTransfer,
    arrivalTransferKey,
    departureTransferKey,
    hasTransfer,
    priceMap,
    discountNights,
    discountPercent,
    discountExcursions,
  ]);

  const transferSummaryLabel = (
    cat: TransferCategory,
    cars: CarCount,
    direction: "arrival" | "departure"
  ): string => {
    if (cat === "none") return isDe ? "Kein Transfer" : "No transfer";
    const key = transferKeyOf(cat, cars);
    const amount = priceOf(key);
    const arrival = direction === "arrival";

    // Richtung und Transferart bestimmen die Formulierung (deutsch/englisch).
    let route: string;
    if (isDe) {
      if (cat === "region") {
        route = arrival ? "Von Nord-/Ost-/Westbali" : "Nach Nord-/Ost-/Westbali";
      } else if (cat === "hotel") {
        route = arrival ? "Von einem Hotel (Umkreis 50 km)" : "Zum Hotel (Umkreis 50 km)";
      } else {
        route = arrival ? "Vom Flughafen" : "Zum Flughafen";
      }
    } else {
      if (cat === "region") {
        route = arrival ? "From North/East/West Bali" : "To North/East/West Bali";
      } else if (cat === "hotel") {
        route = arrival ? "From a hotel (within 50 km)" : "To the hotel (within 50 km)";
      } else {
        route = arrival ? "From the airport" : "To the airport";
      }
    }
    const autoWord = isDe ? (cars === 1 ? "Auto" : "Autos") : cars === 1 ? "car" : "cars";
    const personWord = isDe ? "Personen" : "people";
    return `${route} · ${cars} ${autoWord} (max. ${cars * 3} ${personWord}) · ${money(amount)}`;
  };

  const flightText = (f: { airline: string; number: string; time: string }): string =>
    [f.airline.trim(), f.number.trim(), f.time].filter(Boolean).join(" · ");

  // Scroll back up to the booking steps whenever the step changes.
  useEffect(() => {
    if (firstStepRender.current) {
      firstStepRender.current = false;
      return;
    }
    stepsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [step]);

  // Scroll to the confirmation once the request was sent successfully.
  useEffect(() => {
    if (status.type === "success") {
      successRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [status.type]);

  const goNext = () => {
    setStepError(null);
    if (step === 0) {
      if (!villa) {
        setStepError(isDe ? "Bitte wählen Sie eine Villa aus." : "Please select a villa.");
        return;
      }
      setStep(1);
    } else if (step === 1) {
      if (!start || !end) {
        setStepError(
          isDe
            ? "Bitte wählen Sie An- und Abreisedatum."
            : "Please select arrival and departure dates."
        );
        return;
      }
      if (nights <= 0) {
        setStepError(
          isDe
            ? "Das Abreisedatum muss nach dem Anreisedatum liegen."
            : "Departure date must be after arrival date."
        );
        return;
      }
      setStep(2);
    } else if (step === 2) {
      const usedDays =
        excursions.day_trip.days +
        (excursions.rafting > 0 ? 1 : 0) +
        (excursions.sailing > 0 ? 1 : 0);
      if (usedDays > excursionDayBudget) {
        setStepError(
          isDe
            ? `An ${tourBlockedInStay.length} ${
                tourBlockedInStay.length === 1 ? "Tag" : "Tagen"
              } Ihres Aufenthalts finden keine Touren statt. Bitte reduzieren Sie die Ausflüge auf ${excursionDayBudget} ${
                excursionDayBudget === 1 ? "Tag" : "Tage"
              }.`
            : `${tourBlockedInStay.length} day(s) of your stay are not available for tours. Please reduce excursions to ${excursionDayBudget} day(s).`
        );
        return;
      }
      setStep(hasTransfer ? 3 : 4);
    } else if (step === 3) {
      setStep(4);
    } else if (step === 4) {
      if (!firstname.trim() || !lastname.trim() || !email.trim()) {
        setStepError(isDe ? "Bitte füllen Sie Name und E-Mail aus." : "Please fill in name and email.");
        return;
      }
      setStep(5);
    }
  };

  const goBack = () => {
    setStepError(null);
    setStep((prev) => {
      if (prev === 4 && !hasTransfer) return 2;
      return prev > 0 ? ((prev - 1) as Step) : 0;
    });
  };

  const resetForm = () => {
    setStep(0);
    setVilla("");
    setStart("");
    setEnd("");
    setPersons(2);
    setExcursions({
      day_trip: { days: 0, persons: 2 },
      massage: { days: 0, persons: 2 },
      dinner: { days: 0, persons: 2 },
      rafting: 0,
      sailing: 0,
    });
    setArrivalTransfer("none");
    setArrivalCars(1);
    setDepartureTransfer("none");
    setDepartureCars(1);
    setArrivalFlight({ airline: "", number: "", time: "" });
    setDepartureFlight({ airline: "", number: "", time: "" });
    setArrivalPickup("");
    setDeparturePickup("");
    setArrivalPickupTime("");
    setDeparturePickupTime("");
    setFirstname("");
    setLastname("");
    setEmail("");
    setPhone("");
    setMessage("");
    setPreProgramWish("");
    setPostProgramWish("");
    setTerms(false);
    setPrivacy(false);
    setStepError(null);
  };

  const startNewRequest = () => {
    resetForm();
    setStatus({ type: "idle", msg: "" });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot =
      (form.elements.namedItem("website_alt") as HTMLInputElement | null)?.value.trim() ?? "";
    if (honeypot) {
      setStatus({ type: "success", msg: isDe ? "Vielen Dank!" : "Thank you!" });
      resetForm();
      return;
    }

    const nationality =
      (form.elements.namedItem("nationality") as HTMLInputElement | null)?.value.trim() ?? "";

    if (!villa || !start || !end) {
      setStepError(isDe ? "Bitte vervollständigen Sie Ihre Auswahl." : "Please complete your selection.");
      setStep(0);
      return;
    }
    if (!firstname.trim() || !lastname.trim() || !email.trim()) {
      setStepError(isDe ? "Bitte füllen Sie Name und E-Mail aus." : "Please fill in name and email.");
      setStep(4);
      return;
    }
    if (!terms || !privacy) {
      setStepError(
        isDe
          ? "Bitte akzeptieren Sie die AGB und die Datenschutzerklärung."
          : "Please accept the terms and privacy policy."
      );
      return;
    }

    const usedExcursionDays =
      excursions.day_trip.days +
      (excursions.rafting > 0 ? 1 : 0) +
      (excursions.sailing > 0 ? 1 : 0);
    if (usedExcursionDays > excursionDayBudget) {
      setStepError(
        isDe
          ? `An ${tourBlockedInStay.length} ${
              tourBlockedInStay.length === 1 ? "Tag" : "Tagen"
            } Ihres Aufenthalts finden keine Touren statt. Bitte reduzieren Sie die Ausflüge auf ${excursionDayBudget} ${
              excursionDayBudget === 1 ? "Tag" : "Tage"
            }.`
          : `${tourBlockedInStay.length} day(s) of your stay are not available for tours. Please reduce excursions to ${excursionDayBudget} day(s).`
      );
      setStep(2);
      return;
    }

    setStatus({ type: "sending", msg: "" });
    try {
      const { error } = await submitBausteinInquiry({
        villa,
        villa_slug: villaSlug,
        start,
        end,
        persons,
        excursions,
        arrival_transfer: hasTransfer ? arrivalTransferKey : "none",
        departure_transfer: hasTransfer ? departureTransferKey : "none",
        arrival_flight:
          hasTransfer && arrivalTransfer === "airport"
            ? {
                airline: arrivalFlight.airline.trim() || null,
                number: arrivalFlight.number.trim() || null,
                time: arrivalFlight.time || null,
              }
            : null,
        departure_flight:
          hasTransfer && departureTransfer === "airport"
            ? {
                airline: departureFlight.airline.trim() || null,
                number: departureFlight.number.trim() || null,
                time: departureFlight.time || null,
              }
            : null,
        arrival_pickup:
          hasTransfer && (arrivalTransfer === "region" || arrivalTransfer === "hotel")
            ? arrivalPickup.trim() || null
            : null,
        departure_pickup:
          hasTransfer && (departureTransfer === "region" || departureTransfer === "hotel")
            ? departurePickup.trim() || null
            : null,
        arrival_pickup_time:
          hasTransfer && (arrivalTransfer === "region" || arrivalTransfer === "hotel")
            ? arrivalPickupTime || null
            : null,
        departure_pickup_time:
          hasTransfer && (departureTransfer === "region" || departureTransfer === "hotel")
            ? departurePickupTime || null
            : null,
        firstname: firstname.trim(),
        lastname: lastname.trim().toUpperCase(),
        nationality: nationality || null,
        email: email.trim(),
        phone: phone.trim() || null,
        message: message.trim() || null,
        pre_program_wish: preProgramWish.trim() || null,
        post_program_wish: postProgramWish.trim() || null,
        lang: isDe ? "de" : "en",
      });

      if (error) {
        setStatus({
          type: "error",
          msg: error.message || (isDe ? "Ein Fehler ist aufgetreten." : "Something went wrong."),
        });
        return;
      }

      setStatus({
        type: "success",
        msg: isDe
          ? "Vielen Dank! Ihre Reiseanfrage wurde gesendet."
          : "Thank you! Your trip request has been sent.",
      });
      resetForm();
    } catch {
      setStatus({ type: "error", msg: isDe ? "Ein Fehler ist aufgetreten." : "Something went wrong." });
    }
  };

  const hasSelection =
    villa !== "" ||
    start !== "" ||
    excursions.day_trip.days > 0 ||
    excursions.massage.days > 0 ||
    excursions.dinner.days > 0 ||
    excursions.rafting > 0 ||
    excursions.sailing > 0;

  return (
    <div className="bg-background-50 border border-background-200 rounded-lg p-6 md:p-10">
      {status.type === "success" ? (
        <div ref={successRef} className="text-center py-10 md:py-12 scroll-mt-24">
          <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center rounded-full bg-accent-100 text-accent-700">
            <i className="ri-checkbox-circle-line text-4xl"></i>
          </div>
          <h3 className="font-heading text-xl md:text-2xl text-foreground-950 font-medium mb-2">
            {isDe ? "Vielen Dank!" : "Thank you!"}
          </h3>
          <p className="text-foreground-600 text-sm md:text-base max-w-md mx-auto mb-8 leading-relaxed">
            {status.msg}
          </p>
          <button
            type="button"
            onClick={startNewRequest}
            className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-background-50 px-6 py-2.5 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
          >
            <i className="ri-add-line"></i>
            {isDe ? "Neue Anfrage" : "New request"}
          </button>
        </div>
      ) : (
        <>
          {selectedBlocks.length > 0 && (
        <div className="mb-8 bg-background-100 border border-background-200 rounded-md p-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-foreground-700 mr-1 whitespace-nowrap">
              {t("baustein_selected_blocks")}:
            </span>
            {selectedBlocks.map((b) => (
              <span
                key={b}
                className="inline-flex items-center gap-1.5 bg-primary-100 text-primary-900 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap"
              >
                <i className="ri-check-line"></i>
                {t(BLOCK_LABEL_KEYS[b])}
              </span>
            ))}
          </div>
        </div>
      )}

      {priceLoading ? (
        <div className="flex items-center gap-2 text-sm text-foreground-600 py-16 justify-center">
          <i className="ri-loader-4-line animate-spin"></i>
          {isDe ? "Preise werden geladen..." : "Loading prices..."}
        </div>
      ) : priceError ? (
        <div className="text-center py-16">
          <p className="text-sm text-foreground-700 mb-4">
            {isDe
              ? "Die Preise konnten nicht geladen werden. Bitte versuchen Sie es erneut."
              : "Prices could not be loaded. Please try again."}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-background-50 text-sm font-medium px-6 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors"
          >
            <i className="ri-refresh-line"></i>
            {isDe ? "Erneut versuchen" : "Retry"}
          </button>
        </div>
      ) : (
        <>
          {/* Step indicator */}
          <div ref={stepsRef} className="flex flex-wrap items-center gap-2 mb-8 scroll-mt-24">
            {stepItems.map((item, idx) => (
              <div key={item.num} className="flex items-center gap-2">
                <span
                  className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-semibold whitespace-nowrap ${
                    step >= item.num ? "bg-primary-600 text-background-50" : "bg-background-100 text-foreground-500"
                  }`}
                >
                  {idx + 1}
                </span>
                <span
                  className={`text-sm whitespace-nowrap ${
                    step >= item.num ? "text-foreground-900 font-medium" : "text-foreground-500"
                  }`}
                >
                  {item.label}
                </span>
                {idx < stepItems.length - 1 && (
                  <span className="w-8 h-px bg-background-300 mx-1 hidden sm:block"></span>
                )}
              </div>
            ))}
          </div>

          <form onSubmit={handleSubmit} noValidate>
            {/* Step 0: Villa */}
            <div className={step === 0 ? "" : "hidden"}>
              <h3 className="font-heading text-xl md:text-2xl text-foreground-950 font-medium mb-2">
                {isDe ? "Wählen Sie Ihre Villa" : "Choose your villa"}
              </h3>
              <p className="text-foreground-600 text-sm md:text-base mb-6">
                {isDe ? "Bitte wählen Sie Ihre Unterkunft." : "Please choose your accommodation."}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {VILLA_OPTIONS.map((v) => {
                  const active = villa === v.key;
                  return (
                    <button
                      key={v.key}
                      type="button"
                      onClick={() => setVilla(v.key)}
                      className={`text-left rounded-lg border p-5 transition-colors cursor-pointer ${
                        v.wide ? "sm:col-span-2" : ""
                      } ${
                        active
                          ? "border-primary-500 bg-primary-50"
                          : "border-background-200 bg-background-50 hover:border-primary-300"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-heading text-base text-foreground-950 font-medium">
                          {labelOf(v.key)}
                        </span>
                        <i
                          className={`text-lg ${
                            active
                              ? "ri-checkbox-circle-fill text-primary-600"
                              : "ri-checkbox-blank-circle-line text-foreground-300"
                          }`}
                        ></i>
                      </div>
                      <p className="text-sm text-foreground-600 mt-1.5">
                        {money(priceOf(v.key))} {isDe ? "pro Nacht" : "per night"} · {isDe ? "max." : "max."}{" "}
                        {v.maxPersons} {isDe ? "Personen" : "people"}
                      </p>
                      {v.key === "villa_gesamt" && (
                        <p className="mt-2 flex items-start gap-1.5 text-xs text-accent-700">
                          <i className="ri-information-line mt-0.5"></i>
                          <span>
                            {isDe
                              ? "Alle drei Villen zusammen – Festpreis pro Nacht inkl. Frühstück für bis zu 12 Personen."
                              : "All three villas together – flat rate per night incl. breakfast for up to 12 people."}
                          </span>
                        </p>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Step 1: Zeitraum & Personen */}
            <div className={step === 1 ? "" : "hidden"}>
              <h3 className="font-heading text-xl md:text-2xl text-foreground-950 font-medium mb-2">
                {isDe ? "Ihr Aufenthalt" : "Your stay"}
              </h3>
              <p className="text-foreground-600 text-sm md:text-base mb-6">
                {isDe
                  ? "Wählen Sie An- und Abreise sowie die Anzahl der Personen."
                  : "Select arrival and departure dates and the number of people."}
              </p>

              <div className="space-y-6 max-w-2xl">
                <div>
                  <label className={labelClass}>{isDe ? "Reisezeitraum" : "Travel period"}</label>
                  <RangeDatePicker
                    start={start}
                    end={end}
                    onStartChange={(v) => {
                      setStart(v);
                      setEnd("");
                    }}
                    onEndChange={setEnd}
                    min={today}
                    max={maxDateStr}
                    startUnavailable={disabledDates}
                    startTurnover={checkInTurnoverDates}
                    endMin={start || today}
                    endMax={maxCheckout}
                    endUnavailable={checkoutBookedDates}
                    endTurnover={checkOutTurnoverDates}
                    turnoverTooltip={
                      isDe
                        ? "Übergangstag — siehe Hinweis unten."
                        : "Turnover day — see note below."
                    }
                    isDe={isDe}
                    startLabel={isDe ? "Ankunft" : "Arrival"}
                    endLabel={isDe ? "Abreise" : "Departure"}
                  />
                  <CheckInOutNote isDe={isDe} className="mt-2" />
                  {isTurnoverDay && (
                    <p className="mt-2 flex items-start gap-2 text-xs text-accent-700 bg-accent-100 border border-accent-200 rounded-md px-3 py-2">
                      <i className="ri-information-line mt-0.5"></i>
                      <span>
                        {isDe
                          ? "Am Anreisetag reist ein anderer Gast ab — Anreise ab 15:00 Uhr möglich."
                          : "On the arrival day, another guest departs — check-in possible from 3:00 PM."}
                      </span>
                    </p>
                  )}
                  {isCheckoutTurnoverDay && (
                    <p className="mt-2 flex items-start gap-2 text-xs text-accent-700 bg-accent-100 border border-accent-200 rounded-md px-3 py-2">
                      <i className="ri-information-line mt-0.5"></i>
                      <span>
                        {isDe
                          ? "Am Abreisetag reist ein neuer Gast an — bitte bis 12:00 Uhr auschecken."
                          : "On the departure day, another guest arrives — please check out by 12:00 PM."}
                      </span>
                    </p>
                  )}
                </div>

                <div>
                  <label className={labelClass}>{isDe ? "Anzahl Personen" : "Number of people"}</label>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setPersons((p) => Math.max(1, p - 1))}
                      className="w-10 h-10 flex items-center justify-center rounded-md border border-background-300 bg-background-100 text-foreground-800 hover:bg-background-200 cursor-pointer transition-colors"
                    >
                      <i className="ri-subtract-line"></i>
                    </button>
                    <input
                      type="number"
                      min={1}
                      max={maxPersons}
                      value={persons}
                      onChange={(e) =>
                        setPersons(Math.max(1, Math.min(maxPersons, parseInt(e.target.value) || 1)))
                      }
                      className="w-20 text-center px-2 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    />
                    <button
                      type="button"
                      onClick={() => setPersons((p) => Math.min(maxPersons, p + 1))}
                      className="w-10 h-10 flex items-center justify-center rounded-md border border-background-300 bg-background-100 text-foreground-800 hover:bg-background-200 cursor-pointer transition-colors"
                    >
                      <i className="ri-add-line"></i>
                    </button>
                    <span className="text-xs text-foreground-500">
                      {isDe ? `max. ${maxPersons} Personen` : `max. ${maxPersons} people`}
                    </span>
                  </div>
                </div>

                {nights > 0 && (
                  <div className="bg-background-100 border border-background-200 rounded-md px-4 py-3">
                    <div className="flex items-center justify-between text-sm text-foreground-700">
                      <span>
                        {money(priceOf(villa))} × {nights}{" "}
                        {isDe ? (nights === 1 ? "Nacht" : "Nächte") : nights === 1 ? "night" : "nights"}
                      </span>
                      <span className="font-medium text-foreground-900">{money(calc.villaAmount)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Step 2: Ausflüge */}
            <div className={step === 2 ? "" : "hidden"}>
              <h3 className="font-heading text-xl md:text-2xl text-foreground-950 font-medium mb-2">
                {isDe ? "Ausflüge & Erlebnisse" : "Excursions & experiences"}
              </h3>
              <p className="text-foreground-600 text-sm md:text-base mb-6">
                {isDe
                  ? "Wählen Sie pro Ausflug die Anzahl der teilnehmenden Personen."
                  : "Select the number of participating people per excursion."}
              </p>

              <div className="max-w-2xl">
                {nights <= 0 ? (
                  <p className="text-sm text-secondary-700">
                    {isDe
                      ? "Bitte wählen Sie zuerst Ihren Reisezeitraum."
                      : "Please select your travel period first."}
                  </p>
                ) : (
                  <>
                    <div className="bg-accent-50 border border-accent-100 rounded-md px-4 py-3 mb-5">
                      <p className="text-sm text-foreground-800">
                        <i className="ri-information-line mr-1.5"></i>
                        {isDe
                          ? `Dinner & Massagen sind täglich buchbar. Tagesausflüge, Rafting & Segeln teilen sich zusammen ${excursionDayBudget} ${excursionDayBudget === 1 ? "Tag" : "Tage"} (noch frei: ${remainingExcursionDays}). Ist das Tagesbudget ausgeschöpft, reduzieren Sie einen Tagesausflug, um Rafting oder Segeln hinzuzufügen.`
                          : `Dinner & massages are bookable daily. Day trips, rafting & sailing together share ${excursionDayBudget} ${excursionDayBudget === 1 ? "day" : "days"} (remaining: ${remainingExcursionDays}). If the day budget is used up, reduce a day trip to add rafting or sailing.`}
                      </p>
                      <div className="mt-3 inline-flex items-center bg-accent-50 border border-accent-100 text-accent-900 rounded-md px-3 py-1.5 text-xs font-semibold">
                        <span>
                          {isDe
                            ? `Ab ${discountExcursions} Tagesausflügen & ${discountNights} Nächten: ${discountPercentLabel} % Ermäßigung`
                            : `From ${discountExcursions} day trips & ${discountNights} nights: ${discountPercentLabel}% discount`}
                        </span>
                      </div>
                    </div>

                    {tourBlockedInStay.length > 0 && (
                      <div className="bg-secondary-50 border border-secondary-200 rounded-md px-4 py-3 mb-5">
                        <p className="text-sm text-foreground-800 flex items-start gap-2">
                          <i className="ri-calendar-close-line text-secondary-700 mt-0.5"></i>
                          <span>
                            {isDe
                              ? `An ${tourBlockedInStay.length} ${
                                  tourBlockedInStay.length === 1 ? "Tag" : "Tagen"
                                } Ihres Aufenthalts finden keine Touren statt (Bali Holiday oder Ruhetag): ${tourBlockedInStay
                                  .map(formatBlockedDay)
                                  .join(", ")}. Diese Tage stehen für Tagesausflüge, Rafting und Segeln nicht zur Verfügung – dadurch bleiben Ihnen ${excursionDayBudget} ${
                                  excursionDayBudget === 1 ? "Ausflugstag" : "Ausflugstage"
                                }.`
                              : `No tours take place on ${tourBlockedInStay.length} day(s) of your stay (Bali Holiday or day off): ${tourBlockedInStay
                                  .map(formatBlockedDay)
                                  .join(", ")}. These days are not available for day trips, rafting and sailing – ${excursionDayBudget} excursion day(s) remain.`}
                          </span>
                        </p>
                      </div>
                    )}

                    <div className="space-y-3">
                      {EXCURSION_ITEMS.map((item) => {
                        if (item.kind === "booking") {
                          const bk = item.key as BookingKey;
                          const ppl = excursions[bk];
                          const maxP = Math.min(2, persons);
                          const otherBooked =
                            bk === "rafting"
                              ? excursions.sailing > 0
                                ? 1
                                : 0
                              : excursions.rafting > 0
                              ? 1
                              : 0;
                          const noFreeSlot =
                            ppl === 0 &&
                            excursions.day_trip.days + otherBooked + 1 > excursionDayBudget;
                          const pMaxed = ppl >= maxP || noFreeSlot;
                          const amount = excursionAmount(bk);
                          return (
                            <div
                              key={item.key}
                              className="flex items-center justify-between gap-4 rounded-md border border-background-200 bg-background-50 p-4"
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <div className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md bg-accent-100 text-accent-700">
                                  <i className={`${item.icon} text-lg`}></i>
                                </div>
                                <div className="min-w-0">
                                  <span className="block font-medium text-sm text-foreground-950">
                                    {labelOf(item.key)}
                                  </span>
                                  <span className="block text-xs text-foreground-600 mt-0.5">
                                    {money(priceOf(item.key))} {isDe ? "pro Person" : "per person"}
                                  </span>
                                </div>
                              </div>
                              {amount > 0 && (
                                <span className="text-sm font-semibold text-primary-700 whitespace-nowrap">
                                  {money(amount)}
                                </span>
                              )}
                              <div className="flex items-center gap-2 shrink-0">
                                <button
                                  type="button"
                                  onClick={() => decrementBooking(bk)}
                                  disabled={ppl <= 0}
                                  className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300 bg-background-100 text-foreground-800 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                                >
                                  <i className="ri-subtract-line"></i>
                                </button>
                                <span className="w-8 text-center text-sm font-semibold text-foreground-950">
                                  {ppl}
                                </span>
                                <span className="text-xs text-foreground-600 whitespace-nowrap">
                                  {isDe ? "Pers." : "ppl."}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => incrementBooking(bk)}
                                  disabled={pMaxed}
                                  className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300 bg-background-100 text-foreground-800 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                                >
                                  <i className="ri-add-line"></i>
                                </button>
                              </div>
                            </div>
                          );
                        }

                        const mk = item.key as MultiDayKey;
                        const e = excursions[mk];
                        const maxDays = maxDaysFor(mk);
                        const daysMaxed = e.days >= maxDays;
                        const personsMaxed = e.persons >= persons;
                        const amount = excursionAmount(mk);
                        return (
                          <div
                            key={item.key}
                            className="rounded-md border border-background-200 bg-background-50 p-4"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md bg-accent-100 text-accent-700">
                                <i className={`${item.icon} text-lg`}></i>
                              </div>
                              <div className="min-w-0 flex-1">
                                <span className="block font-medium text-sm text-foreground-950">
                                  {labelOf(item.key)}
                                </span>
                                <span className="block text-xs text-foreground-600 mt-0.5">
                                  {money(priceOf(item.key))} {isDe ? "pro Person · pro Tag" : "per person · per day"}
                                </span>
                              </div>
                              {amount > 0 && (
                                <span className="text-sm font-semibold text-primary-700 whitespace-nowrap">
                                  {money(amount)}
                                </span>
                              )}
                            </div>

                            <div className="mt-4 grid grid-cols-2 gap-3">
                              <div className="rounded-md border border-background-200 bg-background-100 px-3 py-2.5">
                                <span className="block text-xs text-foreground-600 mb-1.5">
                                  {isDe ? "Tage" : "Days"}
                                </span>
                                <div className="flex items-center justify-between gap-2">
                                  <button
                                    type="button"
                                    onClick={() => decrementDays(mk)}
                                    disabled={e.days <= 0}
                                    className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-800 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                                  >
                                    <i className="ri-subtract-line"></i>
                                  </button>
                                  <span className="w-8 text-center text-sm font-semibold text-foreground-950">
                                    {e.days}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => incrementDays(mk)}
                                    disabled={daysMaxed}
                                    className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-800 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                                  >
                                    <i className="ri-add-line"></i>
                                  </button>
                                </div>
                              </div>

                              <div className="rounded-md border border-background-200 bg-background-100 px-3 py-2.5">
                                <span className="block text-xs text-foreground-600 mb-1.5">
                                  {isDe ? "Personen" : "People"}
                                </span>
                                <div className="flex items-center justify-between gap-2">
                                  <button
                                    type="button"
                                    onClick={() => decrementPersons(mk)}
                                    disabled={e.persons <= 1}
                                    className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-800 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                                  >
                                    <i className="ri-subtract-line"></i>
                                  </button>
                                  <span className="w-8 text-center text-sm font-semibold text-foreground-950">
                                    {e.persons}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => incrementPersons(mk)}
                                    disabled={personsMaxed}
                                    className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-800 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                                  >
                                    <i className="ri-add-line"></i>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <p className="text-xs text-foreground-500 mt-3">
                      {isDe
                        ? "Tagesausflüge, Rafting & Segeln teilen sich die verfügbaren Ausflugstage. Rafting & Segeln: 1 oder 2 Personen je Buchung."
                        : "Day trips, rafting & sailing share the available excursion days. Rafting & sailing: 1 or 2 people per booking."}
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Step 3: Transfer */}
            <div className={step === 3 ? "" : "hidden"}>
              <h3 className="font-heading text-xl md:text-2xl text-foreground-950 font-medium mb-2">
                {isDe ? "Transfer" : "Transfer"}
              </h3>
              <p className="text-foreground-600 text-sm md:text-base mb-6">
                {isDe
                  ? "Wählen Sie für An- und Abreise die Transferart und die Anzahl der Autos."
                  : "Choose the transfer type and number of cars for arrival and departure."}
              </p>

              <div className="space-y-6 max-w-2xl">
                <TransferPicker
                  idPrefix="arrival"
                  label={isDe ? "Anreise" : "Arrival"}
                  labelClassName="inline-block bg-accent-100 text-accent-900 text-xs tracking-[0.18em] uppercase font-semibold px-3 py-1 rounded-md mb-3"
                  isDe={isDe}
                  category={arrivalTransfer}
                  cars={arrivalCars}
                  onCategoryChange={setArrivalTransfer}
                  onCarsChange={setArrivalCars}
                  flight={arrivalFlight}
                  onFlightChange={setArrivalFlight}
                  pickup={arrivalPickup}
                  onPickupChange={setArrivalPickup}
                  pickupTime={arrivalPickupTime}
                  onPickupTimeChange={setArrivalPickupTime}
                  priceOf={priceOf}
                  money={money}
                  inputClass={inputClass}
                  labelClass={labelClass}
                />
                <TransferPicker
                  idPrefix="departure"
                  label={isDe ? "Abreise" : "Departure"}
                  labelClassName="inline-block bg-accent-100 text-accent-900 text-xs tracking-[0.18em] uppercase font-semibold px-3 py-1 rounded-md mb-3"
                  isDe={isDe}
                  category={departureTransfer}
                  cars={departureCars}
                  onCategoryChange={setDepartureTransfer}
                  onCarsChange={setDepartureCars}
                  flight={departureFlight}
                  onFlightChange={setDepartureFlight}
                  pickup={departurePickup}
                  onPickupChange={setDeparturePickup}
                  pickupTime={departurePickupTime}
                  onPickupTimeChange={setDeparturePickupTime}
                  priceOf={priceOf}
                  money={money}
                  inputClass={inputClass}
                  labelClass={labelClass}
                />
              </div>
            </div>

            {/* Step 4: Kontakt */}
            <div className={step === 4 ? "" : "hidden"}>
              <h3 className="font-heading text-xl md:text-2xl text-foreground-950 font-medium mb-2">
                {isDe ? "Ihre Kontaktdaten" : "Your contact details"}
              </h3>
              <p className="text-foreground-600 text-sm md:text-base mb-6">
                {isDe ? "Damit wir Ihnen ein Angebot senden können." : "So we can send you an offer."}
              </p>

              <div className="space-y-5 max-w-2xl">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="baustein_firstname" className={labelClass}>
                      {isDe ? "Vorname" : "First name"} <span className="text-accent-600">*</span>
                    </label>
                    <input
                      id="baustein_firstname"
                      name="firstname"
                      type="text"
                      value={firstname}
                      onChange={(e) => setFirstname(e.target.value)}
                      placeholder={isDe ? "Vorname" : "First name"}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="baustein_lastname" className={labelClass}>
                      {isDe ? "Nachname" : "Last name"} <span className="text-accent-600">*</span>
                    </label>
                    <input
                      id="baustein_lastname"
                      name="lastname"
                      type="text"
                      value={lastname}
                      onChange={(e) => setLastname(e.target.value.toUpperCase())}
                      placeholder={isDe ? "Nachname" : "Last name"}
                      className={`${inputClass} uppercase`}
                    />
                  </div>
                </div>

                <div>
                  <label className={labelClass}>{isDe ? "Nationalität" : "Nationality"}</label>
                  <NationalitySelect id="baustein_nationality" name="nationality" isDe={isDe} className={inputClass} />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="baustein_email" className={labelClass}>
                      {isDe ? "E-Mail" : "Email"} <span className="text-accent-600">*</span>
                    </label>
                    <input
                      id="baustein_email"
                      name="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                      placeholder={isDe ? "sie@beispiel.de" : "you@example.com"}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="baustein_phone" className={labelClass}>
                      {isDe ? "Telefon" : "Phone"}
                    </label>
                    <input
                      id="baustein_phone"
                      name="phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder={isDe ? "+49 ..." : "+49 ..."}
                      className={inputClass}
                    />
                  </div>
                </div>

                {hasVornach && (
                  <div className="space-y-5">
                    <div>
                      <label htmlFor="baustein_pre_program" className={labelClass}>
                        {isDe
                          ? "Vorprogramm – Teilen Sie uns Ihre Wünsche mit"
                          : "Pre-program – share your wishes with us"}
                      </label>
                      <textarea
                        id="baustein_pre_program"
                        name="pre_program_wish"
                        rows={3}
                        maxLength={500}
                        value={preProgramWish}
                        onChange={(e) => setPreProgramWish(e.target.value)}
                        placeholder={isDe
                          ? "Ihre Wünsche für das Vorprogramm..."
                          : "Your wishes for the pre-program..."}
                        className={`${inputClass} resize-none`}
                      ></textarea>
                    </div>
                    <div>
                      <label htmlFor="baustein_post_program" className={labelClass}>
                        {isDe
                          ? "Nachprogramm – Teilen Sie uns Ihre Wünsche mit"
                          : "Post-program – share your wishes with us"}
                      </label>
                      <textarea
                        id="baustein_post_program"
                        name="post_program_wish"
                        rows={3}
                        maxLength={500}
                        value={postProgramWish}
                        onChange={(e) => setPostProgramWish(e.target.value)}
                        placeholder={isDe
                          ? "Ihre Wünsche für das Nachprogramm..."
                          : "Your wishes for the post-program..."}
                        className={`${inputClass} resize-none`}
                      ></textarea>
                    </div>
                  </div>
                )}

                <div>
                  <label htmlFor="baustein_message" className={labelClass}>
                    {isDe ? "Zusätzliche Nachricht" : "Additional message"}
                  </label>
                  <textarea
                    id="baustein_message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={isDe ? "Besondere Wünsche, Essenswünsche, ..." : "Special wishes, food preferences, ..."}
                    className={`${inputClass} resize-none`}
                  ></textarea>
                  <p className="text-xs text-foreground-500 mt-1">
                    {isDe ? "Maximal 500 Zeichen" : "Maximum 500 characters"}
                  </p>
                </div>


              </div>
            </div>

            {/* Step 5: Bestätigen */}
            <div className={step === 5 ? "" : "hidden"}>
              <h3 className="font-heading text-xl md:text-2xl text-foreground-950 font-medium mb-6">
                {isDe ? "Ihre Zusammenstellung" : "Your selection"}
              </h3>

              <div className="bg-background-100 rounded-md p-5 md:p-6 space-y-4">
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between text-foreground-700">
                    <span>
                      {labelOf(villa)} ({nights}{" "}
                      {isDe ? (nights === 1 ? "Nacht" : "Nächte") : nights === 1 ? "night" : "nights"})
                    </span>
                    <span className="font-medium">{money(calc.villaAmount)}</span>
                  </div>

                  {EXCURSION_ITEMS.map((item) => {
                    const display = excursionDisplay(item.key);
                    if (!display) return null;
                    return (
                      <div key={item.key} className="flex items-center justify-between text-foreground-700">
                        <span>
                          {display.label} ({display.detail})
                        </span>
                        <span className="font-medium">
                          {money(display.amount)}
                        </span>
                      </div>
                    );
                  })}

                  {hasTransfer && (
                    <>
                      <div className="flex items-center justify-between text-foreground-700">
                        <span>
                          {isDe ? "Anreise" : "Arrival"}: {transferSummaryLabel(arrivalTransfer, arrivalCars, "arrival")}
                        </span>
                        <span className="font-medium">{money(calc.arrivalAmount)}</span>
                      </div>
                      {arrivalTransfer === "airport" && flightText(arrivalFlight) && (
                        <div className="flex items-center gap-1.5 text-xs text-foreground-500 -mt-1 pl-0.5">
                          <i className="ri-flight-land-line"></i>
                          <span>{flightText(arrivalFlight)}</span>
                        </div>
                      )}
                      {(arrivalTransfer === "hotel" || arrivalTransfer === "region") && arrivalPickup.trim() && (
                        <div className="flex items-center gap-1.5 text-xs text-foreground-500 -mt-1 pl-0.5">
                          <i className="ri-hotel-line"></i>
                          <span>{isDe ? "Hotel" : "Hotel"}: {arrivalPickup.trim()}</span>
                        </div>
                      )}
                      {(arrivalTransfer === "region" || arrivalTransfer === "hotel") && arrivalPickupTime && (
                        <div className="flex items-center gap-1.5 text-xs text-foreground-500 -mt-1 pl-0.5">
                          <i className="ri-time-line"></i>
                          <span>{isDe ? "Abholzeit" : "Pickup time"}: {arrivalPickupTime}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between text-foreground-700">
                        <span>
                          {isDe ? "Abreise" : "Departure"}: {transferSummaryLabel(departureTransfer, departureCars, "departure")}
                        </span>
                        <span className="font-medium">{money(calc.departureAmount)}</span>
                      </div>
                      {departureTransfer === "airport" && flightText(departureFlight) && (
                        <div className="flex items-center gap-1.5 text-xs text-foreground-500 -mt-1 pl-0.5">
                          <i className="ri-flight-takeoff-line"></i>
                          <span>{flightText(departureFlight)}</span>
                        </div>
                      )}
                      {(departureTransfer === "hotel" || departureTransfer === "region") && departurePickup.trim() && (
                        <div className="flex items-center gap-1.5 text-xs text-foreground-500 -mt-1 pl-0.5">
                          <i className="ri-hotel-line"></i>
                          <span>{isDe ? "Hotel" : "Hotel"}: {departurePickup.trim()}</span>
                        </div>
                      )}
                      {(departureTransfer === "region" || departureTransfer === "hotel") && departurePickupTime && (
                        <div className="flex items-center gap-1.5 text-xs text-foreground-500 -mt-1 pl-0.5">
                          <i className="ri-time-line"></i>
                          <span>{isDe ? "Abreisezeit" : "Departure time"}: {departurePickupTime}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="border-t border-background-200 pt-3 flex items-center justify-between text-sm">
                  <span className="text-foreground-700">{isDe ? "Zwischensumme" : "Subtotal"}</span>
                  <span className="font-medium text-foreground-900">{money(calc.subtotal)}</span>
                </div>

                {calc.discountApplied && (
                  <div className="flex items-center justify-between gap-3 text-sm text-accent-900 bg-accent-50 border border-accent-100 rounded-md px-3 py-2">
                    <span>
                      {isDe
                        ? `Ermäßigung (ab ${discountNights} Nächten & ${discountExcursions} Tagesausflügen, ${discountPercentLabel} %)`
                        : `Discount (from ${discountNights} nights & ${discountExcursions} day trips, ${discountPercentLabel}%)`}
                    </span>
                    <span className="font-medium whitespace-nowrap">−{money(calc.discount)}</span>
                  </div>
                )}

                <div className="border-t border-background-200 pt-3 flex items-center justify-between">
                  <span className="font-heading text-lg text-foreground-950 font-medium">
                    {isDe ? "Gesamtpreis" : "Total price"}
                  </span>
                  <span className="font-heading text-2xl text-primary-700 font-semibold">
                    {money(calc.total)}
                  </span>
                </div>

                <p className="text-xs text-foreground-500">
                  {isDe
                    ? "* Unverbindliche Kalkulation – die endgültige Bestätigung erhalten Sie von Bali VW Tour."
                    : "* Non-binding calculation – final confirmation by Bali VW Tour."}
                </p>
              </div>

              <div className="mt-6 space-y-4">
                <div className="flex items-start gap-3">
                  <input
                    id="baustein_terms"
                    type="checkbox"
                    checked={terms}
                    onChange={(e) => setTerms(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-background-300 text-primary-600 focus:ring-primary-400 cursor-pointer"
                  />
                  <label htmlFor="baustein_terms" className="text-sm text-foreground-700 leading-relaxed cursor-pointer">
                    {isDe ? (
                      <>
                        Ich habe die{" "}
                        <LocalizedLink
                          to="/impressum"
                          onClick={(e) => e.stopPropagation()}
                          className="text-primary-700 underline hover:text-primary-800 cursor-pointer"
                        >
                          AGB
                        </LocalizedLink>{" "}
                        gelesen und akzeptiere sie.
                      </>
                    ) : (
                      <>
                        I have read and accept the{" "}
                        <LocalizedLink
                          to="/impressum"
                          onClick={(e) => e.stopPropagation()}
                          className="text-primary-700 underline hover:text-primary-800 cursor-pointer"
                        >
                          terms and conditions
                        </LocalizedLink>
                        .
                      </>
                    )}{" "}
                    <span className="text-accent-600">*</span>
                  </label>
                </div>
                <div className="flex items-start gap-3">
                  <input
                    id="baustein_privacy"
                    type="checkbox"
                    checked={privacy}
                    onChange={(e) => setPrivacy(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-background-300 text-primary-600 focus:ring-primary-400 cursor-pointer"
                  />
                  <label htmlFor="baustein_privacy" className="text-sm text-foreground-700 leading-relaxed cursor-pointer">
                    {isDe ? (
                      <>
                        Ich habe die{" "}
                        <LocalizedLink
                          to="/privacy"
                          onClick={(e) => e.stopPropagation()}
                          className="text-primary-700 underline hover:text-primary-800 cursor-pointer"
                        >
                          Datenschutzerklärung
                        </LocalizedLink>{" "}
                        gelesen und bin einverstanden.
                      </>
                    ) : (
                      <>
                        I have read the{" "}
                        <LocalizedLink
                          to="/privacy"
                          onClick={(e) => e.stopPropagation()}
                          className="text-primary-700 underline hover:text-primary-800 cursor-pointer"
                        >
                          privacy policy
                        </LocalizedLink>{" "}
                        and agree.
                      </>
                    )}{" "}
                    <span className="text-accent-600">*</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Honeypot */}
            <input
              type="text"
              name="website_alt"
              className="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            {stepError && (
              <p className="flex items-center gap-2 text-sm text-secondary-700 mt-4">
                <i className="ri-error-warning-line text-accent-600"></i>
                {stepError}
              </p>
            )}

            {status.type === "error" && (
              <p className="flex items-center gap-2 text-sm text-secondary-700 mt-4">
                <i className="ri-error-warning-line text-accent-600"></i>
                {status.msg}
              </p>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between gap-4 mt-8">
              {step > 0 ? (
                <button
                  type="button"
                  onClick={goBack}
                  className="inline-flex items-center gap-1.5 text-sm text-foreground-700 hover:text-foreground-950 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-s-line text-lg"></i>
                  {isDe ? "Zurück" : "Back"}
                </button>
              ) : (
                <span></span>
              )}

              {step < 5 ? (
                <button
                  type="button"
                  onClick={goNext}
                  className="inline-flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 text-background-50 px-8 py-3 rounded-md font-medium whitespace-nowrap cursor-pointer transition-colors"
                >
                  {isDe ? "Weiter" : "Next"}
                  <i className="ri-arrow-right-line"></i>
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={status.type === "sending"}
                  className="inline-flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed text-background-50 px-8 py-3 rounded-md font-medium whitespace-nowrap cursor-pointer transition-colors"
                >
                  {status.type === "sending" && <i className="ri-loader-4-line animate-spin"></i>}
                  {status.type === "sending"
                    ? isDe
                      ? "Wird gesendet..."
                      : "Sending..."
                    : isDe
                    ? "Anfrage senden"
                    : "Send request"}
                </button>
              )}
            </div>

            {hasSelection && step < 5 && (
              <div className="mt-6 flex items-center justify-between gap-4 rounded-md border border-primary-200 bg-primary-50 px-4 py-3">
                <span className="text-sm font-medium text-foreground-900">
                  {isDe ? "Gesamtpreis" : "Total price"}
                  {calc.discountApplied && (
                    <span className="ml-2 text-xs text-accent-700">
                      {isDe
                        ? `(inkl. ${discountPercentLabel} % Ermäßigung)`
                        : `(incl. ${discountPercentLabel}% discount)`}
                    </span>
                  )}
                </span>
                <span className="text-lg font-heading font-semibold text-primary-700 whitespace-nowrap">
                  {money(calc.total)}
                </span>
              </div>
            )}
          </form>
        </>
      )}
        </>
      )}
    </div>
  );
}